import { useRef, useState } from "react";
import { Info } from "lucide-react";
import { asset } from "@/lib/api";
import { KIND_ICON } from "./HotspotLayer";

export function floorsOf(house) {
  return [...(house.floorplan_url ? [{ id: "main", name: "Main floor", url: house.floorplan_url }] : []), ...(house.floorplans || [])];
}

const onFloor = (h, floorId) => (h.plan_pos?.floor || "main") === floorId;

export function FloorTabs({ floors, cur, onChange }) {
  if (floors.length < 2) return null;
  return (
    <div className="flex gap-1 rounded-full bg-white/10 p-1 mb-3" data-testid="floor-tabs">
      {floors.map((f) => <button key={f.id} onClick={() => onChange(f.id)} className={`h-8 px-4 rounded-full text-xs font-semibold ${cur === f.id ? "bg-white text-slate-900" : "text-white hover:bg-white/10"}`} data-testid={`floor-tab-${f.id}`}>{f.name}</button>)}
    </div>
  );
}

export function FloorPlanPins({ image, floorId = "main", hotspots, onPick, editable = false, onPlace, armed = null, logo = "" }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);
  const rooms = hotspots;
  const posFrom = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)), y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)), floor: floorId };
  };
  const up = (e) => {
    if (drag) { onPlace?.(drag.h, posFrom(e)); setDrag(null); return; }
    if (editable && armed) onPlace?.(armed, posFrom(e));
  };
  return (
    <div ref={ref} className={`relative inline-block max-h-full ${editable && armed ? "cursor-crosshair" : ""}`} onPointerUp={up} onPointerMove={(e) => drag && setDrag({ ...drag, p: posFrom(e) })} data-testid="floorplan-pins">
      <img src={asset(image)} alt="Floor plan" className="max-h-[calc(100dvh-10rem)] w-auto max-w-[min(100%,1400px)] min-w-[min(100%,720px)] object-contain rounded-2xl select-none bg-white" draggable={false} />
      {rooms.filter((h) => (h.plan_pos && onFloor(h, floorId)) || drag?.h.id === h.id).map((h) => {
        const p = drag?.h.id === h.id && drag.p ? drag.p : h.plan_pos;
        const Icon = KIND_ICON[h.kind] || Info;
        const room = h.kind === "room";
        return (
          <button key={h.id} type="button" title={h.title}
            onPointerDown={(e) => { if (editable) { e.stopPropagation(); setDrag({ h, p: null }); } }}
            onClick={() => !editable && onPick?.(h)}
            className={`group absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 rounded-full shadow-xl py-1 text-xs font-semibold hover:scale-105 hover:z-10 transition-transform touch-none ${room ? "bg-white text-slate-900 pl-1 pr-3" : "bg-slate-900 text-white px-1"}`}
            style={{ left: `${p.x}%`, top: `${p.y}%` }} data-testid={`floorplan-pin-${h.id}`}>
            {logo ? <img src={asset(logo)} alt="" className="w-6 h-6 rounded-full object-contain bg-white" /> : <span className={`w-6 h-6 rounded-full grid place-items-center ${room ? "bg-bp text-white" : "bg-ba text-white"}`}><Icon className="w-3.5 h-3.5" /></span>}
            <span className={room || editable ? "" : "hidden group-hover:inline pr-2"}>{h.title}</span>
          </button>
        );
      })}
    </div>
  );
}

export function FloorPlanViewer({ house, hotspots, onPick, logo = "" }) {
  const floors = floorsOf(house);
  const [cur, setCur] = useState(floors[0]?.id);
  const f = floors.find((x) => x.id === cur) || floors[0];
  if (!f) return <p className="text-white/70">Floor plan not added yet.</p>;
  return (
    <div className="flex flex-col items-center">
      <FloorTabs floors={floors} cur={f.id} onChange={setCur} />
      <FloorPlanPins image={f.url} floorId={f.id} hotspots={hotspots} onPick={onPick} logo={logo} />
    </div>
  );
}
